import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import { format, parseISO } from 'date-fns';
import dayjs from 'dayjs';
import DatePickerComponent from './DatePickerComponent';
import api from './axiosConfig';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const gbp = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);

// UK tax year runs 6 April to 5 April.
const taxYearStart = (now, yearsBack = 0) => {
  const y = now.getFullYear() - (now < new Date(now.getFullYear(), 3, 6) ? 1 : 0) - yearsBack;
  return new Date(y, 3, 6);
};

const periodRange = (key, custom) => {
  const now = new Date();
  let start;
  let end = now;
  if (key === 'custom' && custom && custom.start && custom.end) {
    return { start: custom.start.format('YYYY-MM-DD'), end: custom.end.format('YYYY-MM-DD') };
  }
  if (key === 'month') start = new Date(now.getFullYear(), now.getMonth(), 1);
  else if (key === 'year') start = new Date(now.getFullYear(), 0, 1);
  else if (key === '12m') start = new Date(now.getFullYear(), now.getMonth() - 11, 1); // last 12 calendar months
  else if (key === 'taxyear') start = taxYearStart(now);
  else if (key === 'lasttaxyear') {
    start = taxYearStart(now, 1);
    end = new Date(start.getFullYear() + 1, 3, 5);
  }
  else start = new Date(now.getFullYear(), now.getMonth() - 2, 1); // last 3 months
  return { start: format(start, 'yyyy-MM-dd'), end: format(end, 'yyyy-MM-dd') };
};

// "What's left after you've been paid": profit before drawings, less what the owner
// took out. Profit comes from the Profit & Loss report itself, so the two always match.
const LeftAfterDrawings = ({ accountId }) => {
  const [periodKey, setPeriodKey] = useState('3m');
  const [customStart, setCustomStart] = useState(dayjs().startOf('month'));
  const [customEnd, setCustomEnd] = useState(dayjs());
  const customInvalid = periodKey === 'custom' && (!customStart || !customEnd || !customStart.isValid() || !customEnd.isValid() || customEnd.isBefore(customStart));
  const [summary, setSummary] = useState(null);
  const [panelError, setPanelError] = useState(null);
  const [panelLoading, setPanelLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPanelLoading(true);
      setPanelError(null);
      try {
        if (customInvalid) { setPanelLoading(false); return; }
        const { start, end } = periodRange(periodKey, { start: customStart, end: customEnd });
        const response = await api.get('/reports/drawings-summary', {
          params: { start_date: start, end_date: end }
        });
        if (!cancelled) setSummary(response.data);
      } catch (err) {
        console.error('Error loading drawings summary:', err);
        if (!cancelled) setPanelError('Could not load the drawings summary.');
      } finally {
        if (!cancelled) setPanelLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [accountId, periodKey, customStart, customEnd, customInvalid]);

  let status = null;
  if (summary) {
    const { profit_before_drawings: profit, drawings, left_in_business: left,
            drawings_pct_of_profit: pct } = summary;
    if (profit <= 0 && drawings > 0) {
      status = { severity: 'error', text: 'The business made no profit in this period, so everything taken out came from savings or borrowing.' };
    } else if (left < 0) {
      status = { severity: 'error', text: `You took ${gbp(-left)} more than the business made.` };
    } else if (pct !== null && pct >= 75) {
      status = { severity: 'warning', text: `You took ${pct}% of the profit. Tax, loan repayments and a cushion for quiet months still have to come out of what is left.` };
    } else if (pct !== null) {
      status = { severity: 'success', text: `You left ${(100 - pct).toFixed(0)}% of the profit in the business.` };
    }
  }

  // Profit covers the whole period, but bank-based drawings only exist from the first
  // imported statement. If the period reaches back before that, say so and do not
  // show a reassuring message the data cannot support.
  const incomplete = !!(summary && summary.coverage_from && summary.period
    && summary.coverage_from > summary.period.start_date);
  if (incomplete && status && status.severity !== 'error') status = null;

  // Income is often entered late. If the newest income entry is well before the end of
  // the period, profit is missing those days, so say so.
  let incomeBehindDays = 0;
  if (summary && summary.period && summary.income_through) {
    incomeBehindDays = Math.round(
      (parseISO(summary.period.end_date) - parseISO(summary.income_through)) / 86400000
    );
  }
  const incomeBehind = incomeBehindDays > 10;
  // Profit is understated while income is behind, so any verdict (including a red one) is unreliable.
  if (incomeBehind) status = null;

  const sourceBits = summary && summary.sources ? [
    ['Bank payments', summary.sources.bank_personal],
    ['Personal expenses', summary.sources.expenses_personal],
    ['Personal bills', summary.sources.bills_personal],
    ['Earlier drawings records', summary.sources.earlier_drawings_records],
  ].filter(([, v]) => v > 0) : [];

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          What is left after you have been paid
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select value={periodKey} onChange={(e) => setPeriodKey(e.target.value)}>
            <MenuItem value="month">This month</MenuItem>
            <MenuItem value="3m">Last 3 months</MenuItem>
            <MenuItem value="year">This year</MenuItem>
            <MenuItem value="12m">Last 12 months</MenuItem>
            <MenuItem value="taxyear">This tax year</MenuItem>
            <MenuItem value="lasttaxyear">Last tax year</MenuItem>
            <MenuItem value="custom">Custom range</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5, textAlign: 'right' }}>
        {(() => {
          if (customInvalid) return '';
          const r = periodRange(periodKey, { start: customStart, end: customEnd });
          return `${format(parseISO(r.start), 'd MMM yyyy')} to ${format(parseISO(r.end), 'd MMM yyyy')}`;
        })()}
      </Typography>

      {periodKey === 'custom' && (
        <Box sx={{ mt: 1 }}>
          <DatePickerComponent
            startDate={customStart}
            endDate={customEnd}
            setStartDate={setCustomStart}
            setEndDate={setCustomEnd}
          />
          {customInvalid && (
            <Alert severity="warning" sx={{ mb: 1 }}>Please choose a start date and an end date that is on or after it.</Alert>
          )}
        </Box>
      )}

      {panelLoading && !customInvalid && <CircularProgress size={24} />}
      {panelError && <Alert severity="error">{panelError}</Alert>}

      {!panelLoading && !customInvalid && summary && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 1, columnGap: 2, alignItems: 'baseline' }}>
            <Box>
              <Typography variant="body1">Profit before drawings</Typography>
              <Typography variant="caption" color="textSecondary">
                What the business made after its costs. Drawings, tax and loan repayments all come out of this.
              </Typography>
            </Box>
            <Typography variant="h6">{gbp(summary.profit_before_drawings)}</Typography>

            <Box>
              <Typography variant="body1">Less: money you took out</Typography>
              <Typography variant="caption" color="textSecondary">
                Personal payments from the bank and expenses, plus earlier recorded drawings
                {summary.already_counted_as_salary > 0
                  ? `, less ${gbp(summary.already_counted_as_salary)} already counted as director salary`
                  : ''}.
              </Typography>
            </Box>
            <Typography variant="h6">{gbp(summary.drawings)}</Typography>

            <Box>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>Left in the business</Typography>
              {incomeBehind && (
                <Typography variant="caption" color="textSecondary">
                  Not final until the latest income is entered.
                </Typography>
              )}
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: incomeBehind
                  ? 'text.secondary'
                  : summary.left_in_business < 0 ? 'error.main' : 'success.main'
              }}
            >
              {gbp(summary.left_in_business)}
            </Typography>
          </Box>

          {summary.self_assessment_paid > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Self Assessment tax paid in this period: {gbp(summary.self_assessment_paid)}. It is not counted as
              drawings, but it also comes out of the profit: after it, {gbp(summary.left_after_tax_paid)} is left.
            </Typography>
          )}

          {summary.drawings_pct_of_profit !== null && !incomeBehind && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(summary.drawings_pct_of_profit, 100)}
                color={summary.drawings_pct_of_profit >= 100 ? 'error' : summary.drawings_pct_of_profit >= 75 ? 'warning' : 'success'}
                sx={{ height: 10, borderRadius: 5 }}
              />
              <Typography variant="caption" color="textSecondary">
                {summary.drawings_pct_of_profit}% of the profit has been taken out
              </Typography>
            </Box>
          )}

          {incomeBehind && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Income is entered up to {format(parseISO(summary.income_through), 'dd/MM/yyyy')}, so profit for the
              last {incomeBehindDays} days of this period is missing and may be understated.
            </Alert>
          )}

          {incomplete && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Drawings records only go back to {format(parseISO(summary.coverage_from), 'dd/MM/yyyy')},
              so anything taken out before that date is not included, while profit covers the whole period.
              Choose a shorter period to compare like with like.
            </Alert>
          )}

          {status && <Alert severity={status.severity} sx={{ mt: 2 }}>{status.text}</Alert>}

          {sourceBits.length > 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
              How this is made up: {sourceBits.map(([label, v]) => `${label} ${gbp(v)}`).join(' + ')}
              {summary.sources.less_salary_already_counted > 0
                ? `, less ${gbp(summary.sources.less_salary_already_counted)} already counted as director salary`
                : ''}.
              {summary.sources.card_spending_marked_personal > 0
                ? ` Of the bank payments, ${gbp(summary.sources.card_spending_marked_personal)} is card spending marked Personal (not confirmed: if any was for the business, recategorise it).`
                : ''}
            </Typography>
          )}

          {summary.loan_repayments_from_bank > 0 && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loan repayments paid from the bank: {gbp(summary.loan_repayments_from_bank)}. These are not in
              the profit figure, so they also come out of what is left.
            </Typography>
          )}

          {summary.top_payees && summary.top_payees.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Biggest personal payments</Typography>
              {summary.top_payees.map((p) => (
                <Box key={p.description} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{p.description} ({p.count})</Typography>
                  <Typography variant="body2">{gbp(p.amount)}</Typography>
                </Box>
              ))}
            </Box>
          )}

          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
            Profit is taken from your Profit and Loss report, so the two always match. Tax is not taken off
            here. Estimate only, not tax advice.
          </Typography>
        </>
      )}
    </Paper>
  );
};

const Drawings = ({ accountId }) => {
  const [drawings, setDrawings] = useState([]);
  const [monthlyTotals, setMonthlyTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openChartDialog, setOpenChartDialog] = useState(false);
  const [drawingDate, setDrawingDate] = useState(new Date());
  const [drawingAmount, setDrawingAmount] = useState('');
  const [drawingDescription, setDrawingDescription] = useState('');
  const [paymentType, setPaymentType] = useState('BANK');
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  
  const fetchDrawings = async () => {
    setLoading(true);
    try {
      // Get current date as YYYY-MM-DD for default date params
      const today = new Date();
      const startDate = new Date(today.getFullYear(), 0, 1);
      const endDate = today;
      
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
      
      const response = await api.get(`/accounting/drawings`, {
        params: {
          start_date: formattedStartDate,
          end_date: formattedEndDate
        }
      });
      
      if (response.data && response.data.drawings) {
        setDrawings(response.data.drawings);
      }
    } catch (err) {
      console.error('Error fetching drawings:', err);
      setError('Failed to fetch drawings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMonthlyTotals = async (year = new Date().getFullYear()) => {
    try {
      const response = await api.get(`/accounting/drawings/monthly-totals/${year}`);
      
      if (response.data && response.data.monthly_totals) {
        setMonthlyTotals(response.data.monthly_totals);
      }
    } catch (err) {
      console.error('Error fetching monthly drawing totals:', err);
    }
  };
  
  useEffect(() => {
    fetchDrawings();
    fetchMonthlyTotals();
  }, [accountId]);
  
  const handleAddDrawing = async () => {
    try {
      await api.post('/accounting/drawings', {
        date: format(drawingDate, 'yyyy-MM-dd'),
        amount: parseFloat(drawingAmount),
        description: drawingDescription || 'Owner Drawing',
        payment_type: paymentType
      });
      
      setOpenAddDialog(false);
      resetFormFields();
      fetchDrawings(); // Refresh data
      fetchMonthlyTotals(); // Refresh monthly totals
    } catch (err) {
      console.error('Error adding drawing:', err);
      setError('Failed to add drawing. Please try again.');
    }
  };
  
  const handleDeleteDrawing = async (drawingId) => {
    try {
      await api.delete(`/accounting/drawings/${drawingId}`);
      fetchDrawings(); // Refresh data
      fetchMonthlyTotals(); // Refresh monthly totals
    } catch (err) {
      console.error('Error deleting drawing:', err);
      setError('Failed to delete drawing. Please try again.');
    }
  };
  
  const handleReclassifyPersonal = async () => {
    try {
      const response = await api.post('/accounting/drawings/reclassify-personal');
      
      if (response.data && response.data.success) {
        fetchDrawings(); // Refresh data
        fetchMonthlyTotals(); // Refresh monthly totals
        alert(`Successfully reclassified ${response.data.items_reclassified} personal expenditures as drawings.`);
      }
    } catch (err) {
      console.error('Error reclassifying personal expenditures:', err);
      setError('Failed to reclassify personal expenditures. Please try again.');
    }
  };
  
  const handleShowMonthlyChart = async () => {
    await fetchMonthlyTotals(chartYear);
    setOpenChartDialog(true);
  };
  
  const resetFormFields = () => {
    setDrawingDate(new Date());
    setDrawingAmount('');
    setDrawingDescription('');
    setPaymentType('BANK');
  };
  
  const prepareChartData = () => {
    const data = [];
    
    // Sort months chronologically
    const months = Object.keys(monthlyTotals).sort((a, b) => {
      const monthA = new Date(a);
      const monthB = new Date(b);
      return monthA - monthB;
    });
    
    for (const month of months) {
      data.push({
        month,
        amount: monthlyTotals[month]
      });
    }
    
    return data;
  };
  
  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
      </Paper>
    );
  }
  
  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Owner Drawings</Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<AddCircleOutlineIcon />} 
            onClick={() => setOpenAddDialog(true)}
            sx={{ mr: 1 }}
          >
            Add Drawing
          </Button>
          <Button 
            variant="outlined"
            startIcon={<BarChartIcon />}
            onClick={handleShowMonthlyChart}
            sx={{ mr: 1 }}
          >
            View Chart
          </Button>
          <Button 
            variant="contained" 
            color="secondary"
            onClick={handleReclassifyPersonal}
          >
            Reclassify Personal
          </Button>
        </Box>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <LeftAfterDrawings accountId={accountId} />
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {drawings.length > 0 ? (
              drawings.map((drawing) => (
                <TableRow key={drawing.id}>
                  <TableCell>{format(parseISO(drawing.date), 'dd/MM/yyyy')}</TableCell>
                  <TableCell>{drawing.description}</TableCell>
                  <TableCell align="right">£{drawing.amount.toFixed(2)}</TableCell>
                  <TableCell>{drawing.payment_type}</TableCell>
                  <TableCell align="center">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteDrawing(drawing.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">No drawings found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Add Drawing Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>Add Owner Drawing</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, pb: 2, minWidth: '300px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Drawing Date"
                value={drawingDate}
                onChange={(newValue) => setDrawingDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
              />
            </LocalizationProvider>
            
            <TextField
              label="Amount"
              type="number"
              value={drawingAmount}
              onChange={(e) => setDrawingAmount(e.target.value)}
              fullWidth
              margin="normal"
              InputProps={{
                startAdornment: <span>£</span>,
              }}
            />
            
            <TextField
              label="Description"
              value={drawingDescription}
              onChange={(e) => setDrawingDescription(e.target.value)}
              fullWidth
              margin="normal"
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentType}
                label="Payment Method"
                onChange={(e) => setPaymentType(e.target.value)}
              >
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="CARD">Card</MenuItem>
                <MenuItem value="BANK">Bank Transfer</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddDrawing} 
            variant="contained" 
            color="primary"
            disabled={!drawingAmount}
          >
            Add Drawing
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Monthly Chart Dialog */}
      <Dialog 
        open={openChartDialog} 
        onClose={() => setOpenChartDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Monthly Drawings for {chartYear}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, pb: 2 }}>
            <TextField
              label="Year"
              type="number"
              value={chartYear}
              onChange={(e) => {
                const year = parseInt(e.target.value);
                setChartYear(year);
                fetchMonthlyTotals(year);
              }}
              margin="normal"
            />
            
            <Box sx={{ height: 400, mt: 2 }}>
              {Object.keys(monthlyTotals).length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={prepareChartData()}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 60,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      angle={-45} 
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis />
                    <Tooltip 
                      formatter={(value) => [`£${value.toFixed(2)}`, 'Amount']}
                    />
                    <Bar dataKey="amount" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1">No data available for {chartYear}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenChartDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Drawings;